<script setup>
import { computed } from "vue";
import { fileTypeIconMap } from "@/api/massEmail";
import i18n from "@/i18n";

const t = (key, params) => i18n.t(key, params);

/**
 * @type {Readonly<{taskInfo:Record<string,any>}>}
 */
const props = defineProps({
  taskInfo: {
    type: Object,
    required: true,
  },
});

const emits = defineEmits(["visibleUsers"]);

/**
 * 获取文件扩展名（小写，不含点）
 * @param {string} fileName 文件名
 * @returns {string} 扩展名，如 'pdf'、'jpg'，无法识别时返回 ''
 */
function getFileExtension(fileName) {
  const idx = fileName.lastIndexOf(".");
  return idx > 0 ? fileName.slice(idx + 1).toLowerCase() : "";
}

/**
 * 获取文件类型图标名，未匹配的扩展名回退为通用文件图标
 * @param {string} fileName
 * @returns {string}
 */
function getFileTypeIcon(fileName) {
  return fileTypeIconMap.get(getFileExtension(fileName)) || "FILE";
}

/**
 * 定时发送时间
 */
const scheduled_send_time = computed(() => {
  if (props.taskInfo.scheduled_send_time) {
    const [timezone, date, time] = props.taskInfo.scheduled_send_time.split(" ");
    return {
      timezone,
      date_time: `${date} ${time}`,
    };
  } else {
    return null;
  }
});

/**
 * 收件人的数量
 * @type {import('vue').ComputedRef<number>}
 */
const contacts = computed(() => {
  return (props.taskInfo?.contacts_csv || 0) + (props.taskInfo?.contacts_input_count || 0);
});

const csv_addr = computed(() => {
  return props.taskInfo.csv_addr[0];
});

function fileDownload(filePath) {
  window.open(filePath);
}
</script>

<template>
  <div class="mass-email-task-settings">
    <div class="task-info">
      <!-- 基本信息 -->
      <div class="task-section">
        <p class="task-section__title">{{ t("massEmail.BasicInfo") }}</p>
        <div class="task-section__row">
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.TaskNameColon") }}</span>
            <span class="task-field__value">{{ props.taskInfo.task_name }}</span>
          </div>
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.SenderEmailColon") }}</span>
            <span class="task-field__value">{{ props.taskInfo.addressee }}</span>
          </div>
          <div class="task-field">
            <span class="task-field__label">已读追踪：</span>
            <span class="task-field__value">{{ props.taskInfo?.open_tracking === 1 ? $t("common.Opened") : $t("common.Closed") }}</span>
          </div>
        </div>
      </div>
      <!-- 邮件内容 -->
      <div class="task-section">
        <p class="task-section__title">{{ t("massEmail.EmailContent") }}</p>
        <div class="task-field">
          <span class="task-field__label">{{ t("massEmail.EmailSubjectColon") }}</span>
          <span class="task-field__value">{{ props.taskInfo.title }}</span>
        </div>
        <p class="task-section__sub-label">{{ t("massEmail.EmailBody") }}</p>
        <div class="email-content-box" v-html="props.taskInfo.content"></div>
        <div class="task-field" v-if="props.taskInfo.annex_addr.length > 0">
          <span class="task-field__label">{{ t("massEmail.UploadAttachmentColon") }}</span>
          <div class="attachment-list" v-if="props.taskInfo?.annex_addr.length>0">
            <div
              class="attachment-item"
              v-for="item in props.taskInfo.annex_addr"
              :key="item.osskey"
              @click="fileDownload(item.preview_url)"
            >
              <LocalSvgIcon
                class="attachment-item__icon"
                :name="getFileTypeIcon(item?.filename)"
                :inherit-color="false"
              />
              <span class="attachment-item__name">{{ item?.filename }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- 目标用户 -->
      <div class="task-section">
        <p class="task-section__title">{{ t("massEmail.TargetUsers") }}</p>
        <div class="task-section__row task-section__row--two-col">
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.EstimatedScaleColon") }}</span>
            <span class="task-field__value">{{ common.toThousandsNew(contacts, 0) }}</span>
          </div>
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.LocalUploadColon") }}</span>
            <div class="attachment-item" v-if="csv_addr" @click="fileDownload(csv_addr.preview_url)">
              <LocalSvgIcon
                class="attachment-item__icon"
                :name="getFileTypeIcon(csv_addr?.filename)"
                :inherit-color="false"
              />
              <span class="attachment-item__name">{{ csv_addr?.filename }}</span>
            </div>
          </div>
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.ManualAddEmailColon") }}</span>
            <span class="task-field__value">
              <el-button type="text" class="jc-text-button" @click="$emit('visibleUsers')">{{
                common.toThousandsNew(props.taskInfo.contacts_input_count, 0)
              }}</el-button>
            </span>
          </div>
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.ImportEmailColon") }}</span>
            <span class="task-field__value">{{ common.toThousandsNew(props.taskInfo.contacts_csv, 0) }}</span>
          </div>
        </div>
      </div>
      <!-- 推送规则 -->
      <div class="task-section task-section--last">
        <p class="task-section__title">{{ t("massEmail.PushRules") }}</p>
        <div class="task-section__row">
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.SendFrequencyColon") }}</span>
            <span class="task-field__value">{{
              t("massEmail.FrequencyPerMail", { count: props.taskInfo?.frequency })
            }}</span>
          </div>
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.SendMethodColon") }}</span>
            <span class="task-field__value">{{
              props.taskInfo.push_type === 0 ? t("massEmail.SendImmediately") : t("massEmail.ScheduledSend")
            }}</span>
          </div>
        </div>
        <div class="task-section__row" v-if="props.taskInfo.push_type === 1">
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.SelectedTimezoneColon") }}</span>
            <span class="task-field__value">{{ scheduled_send_time?.timezone }}</span>
          </div>
          <div class="task-field">
            <span class="task-field__label">{{ t("massEmail.ScheduledTimeColon") }}</span>
            <span class="task-field__value">{{ scheduled_send_time?.date_time }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="email-info">
      <p class="form-box-big-title">{{ t("massEmail.EmailPreview") }}</p>
      <div class="email-preview">
        <div class="email-preview-box-header">
          <p class="header-text">Email</p>
          <div class="header-gyr">
            <div class="header-gyr-item header-gyr-g"></div>
            <div class="header-gyr-item header-gyr-y"></div>
            <div class="header-gyr-item header-gyr-r"></div>
          </div>
        </div>
        <div class="email-preview-box-body">
          <div class="body-user">
            <div class="body-user-to">To</div>
            <div class="body-user-mail">Recipient@example.com</div>
          </div>
          <div class="body-main-title">{{ props.taskInfo.title }}</div>
          <div class="body-text" v-html="props.taskInfo.content"></div>
        </div>
        <div class="email-preview-box-footer">
          <div class="email-preview-box-footer--inner">
            <template v-if="props.taskInfo.annex_addr.length > 0">
              <div class="paperclips" v-for="item in props.taskInfo.annex_addr" :key="item.osskey">
                <LocalSvgIcon name="Paperclip" class="icon-paperclip"/>
                <span class="email-preview-box-footer--attachment-name">{{ item?.filename }}</span>
              </div>
            </template>
            <div v-else class="paperclips">
              <LocalSvgIcon name="Paperclip" class="icon-paperclip"/>
              <span class="email-preview-box-footer--attachment-name">{{ t("massEmail.Attachment") }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$border-color-base: #e0e5e8;
.mass-email-task-settings {
  display: flex;
  min-height: calc(100vh - 225px);

  .task-info {
    flex: 1;
    border-right: 1px solid var(--mc-border-color-v2);
    overflow: auto;

    .task-section {
      padding: 20px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 12px;
      border-bottom: 1px solid #e0e5eb;

      &--last {
        border-bottom: none;
      }

      &__title {
        color: var(--mc-primary-text-color-v2);
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 26px; /* 144.444% */
      }

      &__sub-label {
        color: var(--mc-desc-text-color);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; /* 171.429% */
      }

      &__row {
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(2, 1fr);
      }

      // 目标用户：4 个字段一行放不下，改为每行两个（2 × calc(50% - 12px) + 24px 间距 = 100%）
      &__row--two-col {
        flex-wrap: wrap;
        gap: 12px 24px;

        .task-field {
          flex: 1 1 calc(50% - 12px);
        }
      }
    }

    .task-field {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 171.429% */
      white-space: nowrap;

      &__label {
        flex-shrink: 0;
        color: var(--mc-desc-text-color);
      }

      &__value {
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--mc-primary-text-color-v2);
      }
    }

    .email-content-box {
      box-sizing: border-box;
      width: 100%;
      height: 200px;
      padding: 12px;
      border-radius: var(--mc-border-radius-normal);
      border: 1px solid #e0e5eb;
      background-color: #fafafa;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 142.857% */
      color: var(--mc-primary-text-color-v2);
      overflow: auto;
      // 注意：v-html 渲染的节点不带 scoped 属性，scoped 选择器匹配不到正文内部标签，
      // 正文样式恢复见文件末尾的全局样式块
    }

    .attachment-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .attachment-item {
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;

      &__icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      &__name {
        color: var(--mc-primary-text-color-v2);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; /* 171.429% */
        white-space: nowrap;
      }
    }
  }

  .form-box-big-title {
    color: var(--mc-primary-text-color-v2);
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 26px; /* 144.444% */
  }

  .email-info {
    flex-basis: 480px;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 20px;
    background-color: #fafafa;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .email-preview {
      display: flex;
      width: 440px;
      flex-direction: column;
      border-radius: 12px;
      border: 1px solid var(--mc-border-color-v2);
      overflow: hidden;

      .email-preview-box-header {
        display: flex;
        height: 52px;
        padding: 12px 16px;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
        align-self: stretch;
        background: #f2f2f2;
        box-sizing: border-box;

        .header-text {
          color: var(--mc-primary-text-color-v2);
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: 24px; /* 150% */
        }

        .header-gyr {
          display: flex;
          align-items: center;
          gap: 6px;

          .header-gyr-item {
            width: 10px;
            height: 10px;
            border-radius: 50%;
          }

          .header-gyr-g {
            background-color: #35c659;
          }

          .header-gyr-y {
            background-color: #fbc800;
          }

          .header-gyr-r {
            background-color: #ff5c5f;
          }
        }
      }

      .email-preview-box-body {
        padding: 0 16px 16px 16px;
        background-color: var(--mc-background-main-color);

        .body-user {
          padding: 16px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid $border-color-base;

          .body-user-to {
            color: var(--mc-primary-text-color-v2);
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px; /* 150% */
          }

          .body-user-mail {
            display: flex;
            gap: 8px;
            color: var(--mc-desc-text-color);
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px; /* 150% */
          }
        }

        .body-main-title {
          padding: 16px 0;
          font-size: 18px;
          line-height: 26px;
          font-weight: 500;

          &.empty {
            color: #c0c4cc;
          }
        }

        .body-text {
          font-size: 14px;
          font-style: normal;
          line-height: 22px;

          &.empty {
            color: #c0c4cc;
          }
        }
      }

      .email-preview-box-footer {
        padding: 0 16px 16px;
        
        background-color: var(--mc-background-main-color);
        
        .email-preview-box-footer--inner {
          padding-top: 16px;
          display: flex;
          gap: 12px;
          flex-direction: column;
          border-top: 1px solid $border-color-base;
        }

        .paperclips {
          display: flex;
          padding: 8px 12px;
          border-radius: var(--mc-border-radius-normal);
          background-color: #f3f3f3;
          align-items: center;
          gap: 8px;
          color: var(--mc-info-text-color);
          // 容器宽度跟随内容收缩，超长时封顶在最大宽度，由内部文字省略
          width: fit-content;
          max-width: 248px;
          min-width: 0;

          .icon-paperclip {
            width: 20px;
            height: 20px;
            flex-shrink: 0;
          }

          .email-preview-box-footer--attachment-name {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
/* 邮件正文 v-html 渲染的节点不带 scoped 属性，scoped 样式无法覆盖其内部标签；
   而 element-variables.scss 的全局 reset（ul/li/i/span/p/h1/h2/dl/dt/dd）将
   margin/padding/font-style/list-style 全部归零，会破坏邮件 HTML 的渲染。
   这里用类选择器锚定（特异性 0,1,1 高于全局标签选择器 0,0,1）恢复为浏览器默认值。
   revert 只回滚声明的这几个属性，不影响邮件 HTML 的内联样式与 quill 类样式 */
.email-content-box,
.body-text {
  p,
  ul,
  ol,
  li,
  i,
  em,
  span,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  dl,
  dt,
  dd {
    margin: revert;
    padding: revert;
    font-style: revert;
    list-style: revert;
  }
}
</style>
